import { useUtils } from "@/app/hooks/useUtils";
import dayjs from "dayjs";

export type ReminderItemData = {
  title: string;
  dueDate?: string | null;
};

export type LabReportData={
  title:string;
  tat_date?:string|null;
}

export type VoucherGroupData = {
  group: string;
  total?: number | null;
  used?: number | null;
  expires_on?: string | null;
};

type ReminderStatus = "overdue" | "today" | "upcoming" | "unknown";

const getStatus = (date?: string | null): ReminderStatus => {
  if (!date) return "unknown";
  const due = dayjs(date).startOf("day");
  if (!due.isValid()) return "unknown";
  const today = dayjs().startOf("day");

  if (due.isBefore(today)) return "overdue";
  if (due.isSame(today)) return "today";
  return "upcoming";
};

const formatBadgeText = (status: ReminderStatus, dueDate?: string | null) => {
  if (status === "unknown") return "No due date";
  const parsed = dayjs(dueDate);
  if (!parsed.isValid()) return "No due date";
  if (status === "today") return "Due Today";
  if (status === "overdue") return `Overdue by ${dayjs().diff(parsed, "day")} days`;
  return `Due on ${parsed.format("DD MMM, YY")}`;
};

const ReminderItems = ({
  petName,
  doctorName,
  reminders,
  labreport,
  voucherGroups
}: {
  petName?: string | null;
  doctorName?: string | null;
  reminders?: ReminderItemData[];
  labreport?:LabReportData[];
  voucherGroups?: VoucherGroupData[];
}) => {
  const heading = petName ? `${petName}'s Reminders` : "Reminders";
  const labReportsHeading = petName ? `${petName}'s Pending Lab Reports` : "Lab Pending Reports";
  const displayDoctorName = doctorName?.trim() ? doctorName : null;
  const items = Array.isArray(reminders) ? reminders : [];
  const labReports = Array.isArray(labreport) ? labreport : [];
  const vouchers = Array.isArray(voucherGroups) ? voucherGroups : [];
  const {veticDateFormat} = useUtils();

  return (
    <div className=" font-roboto ">
      {vouchers.length > 0 && (
        <div className="mt-[24px]">
          <div className="flex justify-between items-center bg-[rgba(239,239,239,1)] pr-[60px] pl-[60px] pt-[10px] pb-[10px]">
            <p className="text-[32px] font-medium">Membership Utilization</p>
          </div>

          <div className="mt-[26px] ml-[63px] mr-[58px] rounded-[20px] border border-[rgba(213,213,213,1)] px-[20px]">
            <div>
              {vouchers.map((voucher) => {
                const used = Math.max(Number(voucher.used) || 0, 0);
                const total = Math.max(Number(voucher.total) || 0, 0);
                const usedPercent = total > 0 ? Math.min(used / total, 1) * 100 : 0;

                return (
                  <div key={voucher.group} className="flex h-[90px] items-center justify-between gap-[40px]">
                    <div className="min-w-0">
                      <p className="text-[24px] font-[590] capitalize truncate">{voucher.group}</p>
                      {voucher.expires_on && (
                        <p className="text-[16px] font-medium text-[#6d717f] truncate">{voucher.expires_on}</p>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-[20px]">
                      <div className="w-[600px] h-[10px] bg-[#e3e3e3] rounded-full overflow-hidden">
                        <div className="h-full bg-[#f05b72] rounded-full" style={{ width: `${usedPercent}%` }} />
                      </div>
                      <span className="min-w-[60px] text-right text-[24px] font-[590] text-black">
                        {used}/{total}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mt-[24px] bg-[rgba(239,239,239,1)] pr-[60px] pl-[60px] pt-[10px] pb-[10px]">
        <p className="text-[32px] font-medium">{heading}</p>
        {displayDoctorName ? <p className="text-[24px] font-medium">by {displayDoctorName}</p> : <span />}
      </div>

      <div className="flex justify-between items-center mt-[26px] ml-[63px] mr-[58px]">
        <p className="text-[20px] font-regular uppercase">Reminder</p>
        <p className="text-[20px] font-regular uppercase">Due Date</p>
      </div>

      {items.length === 0 ? (
        <div className="mt-[24px] text-[20px] font-medium text-slate-500">No reminders available.</div>
      ) : (
        <div className="mt-[20px] space-y-[18px] ml-[63px] mr-[58px] ">
          {items.map((item, index) => {
            const status = getStatus(item.dueDate);

            const borderColor =
              status === "overdue" || status === "today"
                ? "border border-[rgba(222,56,56,1)] bg-[rgba(255,0,0,0.03)]"
                : "border-[rgba(213,213,213,1)]";

            const badgeColor =
              status === "overdue" || status === "today"
                ? "bg-[#FF6B6B36] text-[#DE3838]"
                : status === "unknown"
                  ? "bg-slate-100 text-slate-600"
                  : "bg-[rgba(231,245,255,1)] text-[rgba(4,95,166,1)]";

            return (
              <div
                key={`${item.title}-${item.dueDate ?? "no-due-date"}-${index}`}
                className={`grid grid-cols-3 items-center p-[10px] pl-[20px] pr-[20px] rounded-[20px] h-[90px] border ${borderColor}`}
              >
                <p className="text-[24px] font-[590]">{item.title}</p>

                <div className="flex justify-center p-[10px]">
                  <span
                    className={`flex items-center justify-center rounded-[20px] text-[20px] w-[250px] ${badgeColor}`}
                  >
                    {formatBadgeText(status, item.dueDate)}
                  </span>
                </div>

                <p className="text-[24px] font-medium text-right">{veticDateFormat(item.dueDate)}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-[36px] ">
        <div className="flex justify-between items-center bg-[rgba(239,239,239,1)] pr-[60px] pl-[60px] pt-[10px] pb-[10px] ">
          <p className="text-[32px] font-medium">{labReportsHeading}</p>
        </div>

        <div className="flex justify-between items-center mt-[26px] ml-[63px] mr-[58px]">
          <p className="text-[20px] font-regular uppercase">Lab Report</p>
          <p className="text-[20px] font-regular uppercase">Report Deadline</p>
        </div>

          <div className="mt-[16px] space-y-[18px] ml-[63px] mr-[58px]">
            {labReports?.map((report, index) => (
              <div
                key={`${report.title}-${report.tat_date ?? "no-tat"}-${index}`}
                className="grid grid-cols-[1fr_auto] items-center gap-[16px] p-[10px] pl-[20px] pr-[20px] h-[90px] rounded-[20px] border border-[rgba(213,213,213,1)]"
              >
                <p className="text-[24px] font-[590]">{report.title}</p>
                <p className="text-[24px] font-medium text-right whitespace-nowrap">{veticDateFormat(report.tat_date)}</p>
              </div>
            ))}
          </div>
      </div>
    </div>
  );
};

export default ReminderItems;
